import { ReactElement, useState, useRef, useEffect } from 'react';

import Stack from '@mui/material/Stack';
import { useThemeProps } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import colorWheel from '../../styles/colorWheel';

import GratiColorPicker from './GratiColorPicker';
import GratiMarker, { GratiMarkerShape, GratiMarkerSize } from './GratiMarker';

interface GratiMarkerEditorProps {
  shape?: GratiMarkerShape;
  color?: string;
  /** Current symbol/label value. */
  value?: string;
  /** Displayed inside the marker when value is empty. */
  placeholder?: string;
  size?: GratiMarkerSize | string;
  disabled?: boolean;
  onChange?: (newValue: string) => void;
  onColorChange?: (newColor: string) => void;
}

export default function GratiMarkerEditor(inProps: GratiMarkerEditorProps): ReactElement {
  const { shape = 'circle', color, value, placeholder = '?', size = 'lg', disabled = false, onChange, onColorChange } =
    useThemeProps({ props: inProps, name: 'GratiMarkerEditor' });
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value ?? '');
  const [editColor, setEditColor] = useState(color ?? colorWheel[0][0]);
  const editorRef = useRef<HTMLDivElement>(null);

  const displayColor = color ?? colorWheel[0][0];

  // Resolve box size for TextField sizing in edit mode
  const SIZE_MAP: Record<string, string> = {
    xs: '0.75rem', sm: '0.875rem', md: '1.5rem', lg: '2rem', xl: '2.5rem',
  };
  const boxSize = SIZE_MAP[size as string] ?? (size as string);

  const handleClick = () => {
    if (!disabled && !isEditing) {
      setIsEditing(true);
      setEditValue(value ?? '');
      setEditColor(color ?? colorWheel[0][0]);
    }
  };

  const commit = () => {
    setIsEditing(false);
    if (onChange && editValue !== value) onChange(editValue);
    if (onColorChange && editColor !== color) onColorChange(editColor);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      commit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(value ?? '');
      setEditColor(color ?? colorWheel[0][0]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditing && editorRef.current && !editorRef.current.contains(event.target as Node)) {
        commit();
      }
    };
    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, editValue, editColor, value, color]);

  if (isEditing) {
    return (
      <Stack ref={editorRef} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <GratiColorPicker
          value={editColor}
          onChange={setEditColor}
          disableClickAway={true}
          sx={{ width: boxSize, height: boxSize }}
        />
        <Stack spacing={0} sx={{ alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Symbol</Typography>
          <TextField
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            size="small"
            sx={{
              width: '4rem',
              '& .MuiInputBase-root': { height: boxSize },
            }}
            slotProps={{ input: { inputProps: { maxLength: 4 } } }}
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <GratiMarker
      shape={shape}
      color={displayColor}
      label={value || placeholder}
      size={size}
      onClick={disabled ? undefined : handleClick}
      sx={
        !disabled
          ? {
              cursor: 'pointer',
              border: '2px dashed rgba(255,255,255,0.3)',
              '&:hover': { opacity: 0.8, border: '2px dashed rgba(255,255,255,0.6)' },
            }
          : undefined
      }
    />
  );
}
